import { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, ClipLoader, Icon } from '@wf/components';

import { useLicenseContext } from '../../LicenseModeContext';
import ImportModal from '../ImportModal';

import * as licenseTypeService from '../../../../services/licenseTypeApiService';

import { type ILicenseTypeResponse, type ILpcoResponse } from '../../types/response';
import { type IConfig } from '../../types/configs';
import { FileType } from '../../../../constants';

import {
  resetErrors,
  setConfiguration,
  setLicenseType,
  setLicenseTypeLoading,
  setLpco,
  setSelectedLicenseTypeId,
} from '../../store/slice';
import { downloadFile, toasterError, toasterSuccess, toasterValidationError } from '../../../../helpers';
import { licenseTypeAdditionalFieldsSelector, lpcoLoadingSelector } from '../../store/selectors';
import convertFormDataForExport from '../../helpers/convertFormDataForExport';
import checkIsValidLicenseTypeData from '../../helpers/importValidations';
import convertImportedData from '../../helpers/convertImportedData';
import { fileToObject } from '../../../../helpers/fileToObject';

import styles from './ImportExportLicense.module.scss';

function ImportExportLicense() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getValues } = useFormContext();

  const { createMode } = useLicenseContext();

  const lpcoLoading = useSelector(lpcoLoadingSelector);
  const licenseTypeAdditionalFields = useSelector(licenseTypeAdditionalFieldsSelector);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const handleExportData = () => {
    setIsExporting(true);

    const values = getValues();

    const exportData = convertFormDataForExport(values, licenseTypeAdditionalFields);
    downloadFile(
      JSON.stringify(exportData, null, 2),
      `License_${values.licenseType.value as number}_${values.id as string}.json`,
      'text/plain;charset=utf-8'
    );
    toasterSuccess({ title: 'Downloading of data is done successfully.' });
    setIsExporting(false);
  };

  const handleImportData = async (file: File) => {
    setIsImporting(true);
    let importedObject;

    try {
      importedObject = await fileToObject(file);
      checkIsValidLicenseTypeData(importedObject);
    } catch (e) {
      setIsImporting(false);
      toasterError({ title: e as string });

      return;
    }

    let licenseType: ILicenseTypeResponse;
    let configs: IConfig;

    try {
      dispatch(setLicenseTypeLoading(true));

      const { data } = await licenseTypeService.getLicenseTypeByCode(importedObject.licenseType as string);
      licenseType = data.licenseTypeResponse;
      configs = data.configs;
    } catch (e) {
      toasterValidationError(e);
      setIsImporting(false);

      return;
    } finally {
      dispatch(setLicenseTypeLoading(false));
    }

    try {
      const importedLPCO = {
        ...convertImportedData({ importedObject, configs, additionalFieldConfig: licenseType.additionalFields }),
        flow: licenseType.flow,
      };

      dispatch(resetErrors());
      dispatch(setLpco(importedLPCO as ILpcoResponse));
      dispatch(setLicenseType({ licenseTypeResponse: licenseType, configs }));
      dispatch(setConfiguration(configs));

      toasterSuccess({ title: 'Uploading of data is done successfully.' });
      setIsModalOpen(false);
    } catch (e) {
      dispatch(setSelectedLicenseTypeId(null));
      toasterError({ title: e as string });

      return;
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <>
      {createMode && (
        <Button leftIcon={<Icon name="ic_import" />} onClick={() => setIsModalOpen(true)} size="sm" linkButton>
          {t('Upload JSON')}
        </Button>
      )}
      {!createMode && (
        <Button
          loadingIcon={<ClipLoader className={styles.loader} />}
          leftIcon={<Icon name="ic_export" />}
          onClick={handleExportData}
          isLoading={isExporting}
          disabled={lpcoLoading}
          size="sm"
          linkButton
        >
          {t('Download JSON')}
        </Button>
      )}
      {isModalOpen && (
        <ImportModal
          onCancel={() => setIsModalOpen(false)}
          supportedFileTypes={[FileType.JSON]}
          isImportLoading={isImporting}
          supportedMaxFileSizeInMB={5}
          onConfirm={handleImportData}
          title="Upload License Data"
        />
      )}
    </>
  );
}

export default ImportExportLicense;
